import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, User, Calendar, FileText, LogOut, Plus, Award, DollarSign, Edit, RefreshCw, MessageSquare, CreditCard, Stethoscope, Gift } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { ClinicLogo } from '@/hooks/useClinicBranding';
import PaymentMethods from '../components/patient/PaymentMethods';
import PatientDashboardHome from '../components/patient/PatientDashboardHome';
import AppointmentScheduler from '../components/patient/AppointmentScheduler';
import EnhancedAppointmentScheduler from '../components/patient/EnhancedAppointmentScheduler';
import AppointmentHistory from '../components/patient/AppointmentHistory';
import MessagingCenter from '../components/patient/MessagingCenter';
import EnhancedMessagingCenter from '../components/patient/EnhancedMessagingCenter';
import MembershipRenewal from '../components/patient/MembershipRenewal';
import PaymentHistory from '../components/patient/PaymentHistory';
import ProfileManagement from '../components/patient/ProfileManagement';
import OnboardingTour from '../components/help/OnboardingTour';
import HelpWidget from '../components/help/HelpWidget';
import EnhancedDashboard from '../components/patient/EnhancedDashboard';
import PlansCatalog from '../components/patient/PlansCatalog';
import MembershipCard from '../components/patient/MembershipCard';
import BenefitsDashboard from '../components/patient/BenefitsDashboard';
import MemberBenefits from '../components/patient/MemberBenefits';
import MembershipCenter from '../components/patient/MembershipCenter';
import SubscriptionManager from '../components/patient/SubscriptionManager';
import ServiceUsageHistory from '../components/patient/ServiceUsageHistory';
import { SubscriptionFAB, SubscriptionBanner, SubscriptionCTACard } from '../components/patient/SubscriptionCTA';
import LanguageSelector from '../components/patient/LanguageSelector';
import FamilyMembersManager from '../components/patient/FamilyMembersManager';
import LoyaltyDashboard from '../components/patient/Loyalty/LoyaltyDashboard';
import { Users, Crown } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';

const PatientDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode, fonts } = useTheme();
  const [member, setMember] = useState(null);
  const [membershipPlan, setMembershipPlan] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clinicBranding, setClinicBranding] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const isDark = mode === 'dark';

  // Check for refresh parameter (from payment success page)
  useEffect(() => {
    const needsRefresh = searchParams.get('refresh');
    if (needsRefresh === 'true') {
      // Clear the refresh param and force data refresh
      setSearchParams({});
      setDataFetched(false);
      toast.success('Payment completed! Your membership has been updated.');
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    // Prevent multiple fetches
    if (dataFetched) return;
    
    console.log('PatientDashboard mounted, user:', user?.id);
    if (!user) {
      console.error('No user provided to PatientDashboard');
      setError('No user session found. Please login again.');
      setLoading(false);
      return;
    }
    
    setDataFetched(true);
    fetchData();
  }, [user, dataFetched]);

  const fetchData = async () => {
    console.log('PatientDashboard fetchData starting...');
    try {
      // Fetch data in parallel, but handle errors gracefully
      const [memberRes, appointmentsRes, plansRes, brandingRes] = await Promise.all([
        axios.get(`${API}/members/user/${user.id}`).catch((err) => {
          console.log('Member fetch error (may be new user):', err.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/appointments`).catch((err) => {
          console.log('Appointments fetch error:', err.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/plans`).catch((err) => {
          console.log('Plans fetch error:', err.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/clinic/branding`).catch((err) => {
          console.log('Branding fetch error:', err.response?.status);
          return { data: null };
        })
      ]);

      // Set clinic branding
      if (brandingRes.data) {
        setClinicBranding(brandingRes.data);
      }

      console.log('PatientDashboard data fetched:', {
        hasMember: !!memberRes.data,
        appointmentsCount: appointmentsRes.data?.length || 0,
        plansCount: plansRes.data?.length || 0
      });

      if (memberRes.data) {
        setMember(memberRes.data);
        
        if (memberRes.data.membership_plan_id) {
          try {
            const planRes = await axios.get(`${API}/plans/${memberRes.data.membership_plan_id}`);
            setMembershipPlan(planRes.data);
          } catch (err) {
            console.log('Plan fetch error:', err.response?.status);
          }
        }
        
        try {
          const paymentsRes = await axios.get(`${API}/payments/member/${memberRes.data.id}`);
          setPayments(paymentsRes.data || []);
        } catch (err) {
          console.log('Payments fetch error:', err.response?.status);
          setPayments([]);
        }
      }

      setAppointments(appointmentsRes.data || []);
      setAllPlans(plansRes.data || []);
    } catch (err) {
      console.error('PatientDashboard fetchData error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      console.log('PatientDashboard loading complete');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('clinic_id');
    setUser(null);
    navigate('/login');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">{t('dashboard.errorLoading')}</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} className="mr-2">
            {t('common.retry')}
          </Button>
          <Button variant="outline" onClick={handleLogout}>
            {t('common.logout')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen transition-colors duration-300"
      style={{ 
        background: 'var(--main-bg)',
        fontFamily: `var(--font-body, "${fonts?.body || 'Inter'}", sans-serif)`
      }}
    >
      {/* Subscription Banner - Shows for users without membership or expiring soon */}
      {member && (
        <SubscriptionBanner 
          member={member} 
          membershipPlan={membershipPlan} 
          onNavigate={setActiveTab}
        />
      )}

      {/* Header with branded colors - Mobile Optimized */}
      <header 
        className={`border-b sticky top-0 z-10 ${isDark ? 'border-slate-700' : ''}`}
        style={{ 
          background: isDark 
            ? `linear-gradient(135deg, ${clinicBranding?.primary_color || '#14b8a6'}15, ${clinicBranding?.secondary_color || '#0d9488'}10, #1e293b)`
            : `linear-gradient(135deg, ${clinicBranding?.primary_color || '#14b8a6'}08, ${clinicBranding?.secondary_color || '#0d9488'}05, white)`
        }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left side - Logo and Clinic Name */}
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <ClinicLogo 
                logoUrl={clinicBranding?.logo_url}
                clinicName={clinicBranding?.name || 'MyMedMember'}
                size="sm"
                className="flex-shrink-0"
              />
              <div className="min-w-0">
                <span 
                  className="text-sm sm:text-xl font-bold block truncate"
                  style={{ color: clinicBranding?.primary_color || '#14b8a6' }}
                >
                  {clinicBranding?.name || 'MyMedMember'}
                </span>
                <p className={`text-xs hidden sm:block ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {clinicBranding?.name ? t('header.patientPortal') : 'by Clinicax Systems'}
                </p>
              </div>
            </div>
            
            {/* Right side - Theme toggle, Language selector, User info and Logout */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Language Selector */}
              <LanguageSelector variant="compact" />
              
              {/* User info - hidden on small mobile, visible on larger screens */}
              <div className="text-right hidden xs:block">
                <p className={`text-xs sm:text-sm font-medium truncate max-w-[100px] sm:max-w-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {user.full_name}
                </p>
                <p className={`text-xs hidden sm:block ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{member?.patient_id || 'Patient'}</p>
              </div>
              <Button 
                onClick={handleLogout} 
                variant="outline" 
                size="sm" 
                data-testid="logout-btn"
                className={`px-2 sm:px-3 ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}`}
                style={!isDark ? { 
                  borderColor: clinicBranding?.primary_color || '#14b8a6',
                  color: clinicBranding?.primary_color || '#14b8a6'
                } : {}}
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Optimized */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {!member ? (
          <CompleteProfileSection user={user} fetchData={fetchData} allPlans={allPlans} />
        ) : (
          <>
            {/* Welcome Section - Compact on Mobile */}
            <div className="mb-4 sm:mb-8">
              <h1 className={`text-xl sm:text-3xl font-bold mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.welcome', { name: user.full_name?.split(' ')[0] || user.full_name })}
              </h1>
              <p className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                {t('dashboard.manageHealthMembership')}
              </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
              {/* Scrollable Tabs for Mobile */}
              <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                <TabsList className={`inline-flex min-w-max sm:flex-wrap gap-1 p-1 rounded-lg ${isDark ? 'bg-slate-800/80' : 'bg-gray-100/80'}`}>
                  <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm px-3 py-2">
                    {t('tabs.overview')}
                  </TabsTrigger>
                  <TabsTrigger value="my-membership" data-testid="tab-my-membership" className="text-xs sm:text-sm px-3 py-2">
                    <Award className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">{t('tabs.myMembership')}</span>
                    <span className="xs:hidden">{t('tabs.membership')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="benefits" data-testid="tab-benefits" className="text-xs sm:text-sm px-3 py-2">
                    <Gift className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    {t('tabs.benefits')}
                  </TabsTrigger>
                  <TabsTrigger value="services" data-testid="tab-services" className="text-xs sm:text-sm px-3 py-2">
                    <Stethoscope className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    {t('tabs.services')}
                  </TabsTrigger>
                  <TabsTrigger value="appointments" data-testid="tab-appointments" className="text-xs sm:text-sm px-3 py-2">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 sm:hidden" />
                    <span className="hidden sm:inline">{t('tabs.appointments')}</span>
                    <span className="sm:hidden">{t('tabs.appointmentsShort')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="membership-card" data-testid="tab-card" className="text-xs sm:text-sm px-3 py-2">
                    <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 sm:hidden" />
                    <span className="hidden sm:inline">{t('tabs.myCard')}</span>
                    <span className="sm:hidden">{t('tabs.cardShort')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="messages" data-testid="tab-messages" className="text-xs sm:text-sm px-3 py-2">
                    <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('tabs.messages')}</span>
                    <span className="sm:hidden">{t('tabs.messagesShort')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="membership" data-testid="tab-membership" className="text-xs sm:text-sm px-3 py-2">
                    <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 sm:hidden" />
                    <span className="hidden sm:inline">{t('tabs.subscription')}</span>
                    <span className="sm:hidden">{t('tabs.subscriptionShort')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="family" data-testid="tab-family" className="text-xs sm:text-sm px-3 py-2">
                    <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('tabs.family', 'Familia')}</span>
                    <span className="sm:hidden">{t('tabs.familyShort', 'Fam')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="loyalty" data-testid="tab-loyalty" className="text-xs sm:text-sm px-3 py-2">
                    <Crown className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('tabs.loyalty', 'Loyalty')}</span>
                    <span className="sm:hidden">{t('tabs.loyaltyShort', 'Pts')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="profile" data-testid="tab-profile" className="text-xs sm:text-sm px-3 py-2">
                    <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 sm:hidden" />
                    {t('tabs.profile')}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" data-testid="overview-content">
                <EnhancedDashboard 
                  member={member} 
                  membershipPlan={membershipPlan}
                  onNavigate={(tab) => {
                    const tabMapping = {
                      'card': 'membership-card',
                      'appointments': 'appointments',
                      'benefits': 'benefits',
                      'services': 'services',
                      'messages': 'messages',
                      'profile': 'profile',
                      'membership': 'membership',
                      'loyalty': 'loyalty'
                    };
                    setActiveTab(tabMapping[tab] || tab);
                  }}
                />
              </TabsContent>

              <TabsContent value="my-membership" data-testid="my-membership-content">
                <MembershipCenter 
                  member={member} 
                  onScheduleService={(service) => {
                    // Navigate to appointments tab with service pre-selected
                    setActiveTab('appointments');
                    // Store selected service for appointment scheduler
                    localStorage.setItem('selectedService', JSON.stringify(service));
                  }}
                />
              </TabsContent>

              <TabsContent value="benefits" data-testid="benefits-content">
                <MemberBenefits member={member} />
              </TabsContent>

              <TabsContent value="services" data-testid="services-content">
                <ServiceUsageHistory member={member} />
              </TabsContent>

              <TabsContent value="membership-card" data-testid="card-content">
                <MembershipCard member={member} membershipPlan={membershipPlan} />
              </TabsContent>

              <TabsContent value="appointments" data-testid="appointments-content">
                <AppointmentsSection appointments={appointments} member={member} fetchData={fetchData} />
              </TabsContent>

              <TabsContent value="messages" data-testid="messages-content">
                <EnhancedMessagingCenter userRole="patient" />
              </TabsContent>

              <TabsContent value="membership" data-testid="membership-content">
                <MembershipSection member={member} membershipPlan={membershipPlan} allPlans={allPlans} payments={payments} fetchData={fetchData} />
              </TabsContent>

              <TabsContent value="family" data-testid="family-content">
                <FamilyMembersManager />
              </TabsContent>

              <TabsContent value="loyalty" data-testid="loyalty-content">
                <LoyaltyDashboard />
              </TabsContent>

              <TabsContent value="profile" data-testid="profile-content">
                <ProfileSection member={member} user={user} fetchData={fetchData} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      {/* Onboarding Tour for Patients */}
      {member && <OnboardingTour role="patient" />}

      {/* Floating Action Button for Subscription - Mobile friendly */}
      {member && activeTab !== 'membership' && (
        <SubscriptionFAB 
          member={member} 
          membershipPlan={membershipPlan} 
          onNavigate={setActiveTab}
        />
      )}

      {/* Help Widget */}
      <HelpWidget />
    </div>
  );
};

const CompleteProfileSection = ({ user, fetchData, allPlans }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date_of_birth: '',
    phone: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    medical_notes: ''
  });
  const [showPlans, setShowPlans] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members`, {
        user_id: user.id,
        ...formData
      });
      toast.success(t('profile.profileUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('profile.failedToUpdate'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="complete-profile-section">
      {/* Welcome Message */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('dashboard.welcome', { name: user.full_name })}!</h1>
        <p className="text-gray-600">{t('profile.getStartedByChoosing')}</p>
      </div>

      {/* Plans Section - Show First */}
      {showPlans && allPlans && allPlans.length > 0 && (
        <Card className="border-2 border-teal-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-600" />
              {t('membership.choosePlan')}
            </CardTitle>
            <CardDescription>
              {t('membership.selectPlanDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PlansCatalog 
              plans={allPlans} 
              currentPlanId={null}
              onPlanSelected={() => {
                // Plan purchase will create member profile automatically
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-gray-50 px-4 text-gray-500">{t('profile.orCompleteProfileFirst')}</span>
        </div>
      </div>

      {/* Profile Completion Form */}
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.completeProfile')}</CardTitle>
          <CardDescription>{t('profile.provideInfo')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('profile.dateOfBirth')}</Label>
                <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required data-testid="profile-dob-input" />
              </div>
              <div>
                <Label>{t('profile.phone')}</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required data-testid="profile-phone-input" />
              </div>
            </div>
            <div>
              <Label>{t('profile.address')}</Label>
              <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required data-testid="profile-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('profile.emergencyContactName')}</Label>
                <Input value={formData.emergency_contact} onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})} required data-testid="profile-emergency-name-input" />
              </div>
              <div>
                <Label>{t('profile.emergencyContactPhone')}</Label>
                <Input value={formData.emergency_phone} onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})} required data-testid="profile-emergency-phone-input" />
              </div>
            </div>
            <div>
              <Label>{t('profile.medicalNotes')}</Label>
              <Textarea value={formData.medical_notes} onChange={(e) => setFormData({...formData, medical_notes: e.target.value})} placeholder={t('profile.medicalNotesPlaceholder')} data-testid="profile-medical-notes-input" />
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" data-testid="profile-submit-btn">{t('profile.completeProfileButton')}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const OverviewSection = ({ member, membershipPlan, appointments }) => {
  const upcomingAppointments = appointments.filter(a => new Date(a.appointment_date) > new Date() && a.status === 'scheduled');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card data-testid="overview-membership">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Membership Status</CardTitle>
            <Award className="h-4 w-4 text-teal-600" />
          </CardHeader>
          <CardContent>
            <Badge variant={member.membership_status === 'active' ? 'default' : 'secondary'} className="text-lg">
              {member.membership_status}
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="overview-appointments">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Upcoming Appointments</CardTitle>
            <Calendar className="h-4 w-4 text-teal-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{upcomingAppointments.length}</div>
          </CardContent>
        </Card>

        <Card data-testid="overview-patient-id">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Patient ID</CardTitle>
            <User className="h-4 w-4 text-teal-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{member.patient_id}</div>
          </CardContent>
        </Card>
      </div>

      {membershipPlan && (
        <Card>
          <CardHeader>
            <CardTitle>Your Membership Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="text-2xl font-bold text-teal-600">{membershipPlan.name}</h3>
                <p className="text-gray-600">{membershipPlan.description}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Valid Until</p>
                <p className="font-semibold">{member.membership_end_date ? new Date(member.membership_end_date).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Benefits:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {membershipPlan.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-teal-600 mr-2">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AppointmentsSection = ({ appointments, member, fetchData }) => {
  const { t } = useTranslation();
  const [showScheduler, setShowScheduler] = useState(false);

  const handleScheduleSuccess = () => {
    setShowScheduler(false);
    fetchData();
  };

  if (showScheduler) {
    return (
      <div>
        <Button 
          variant="outline" 
          onClick={() => setShowScheduler(false)}
          className="mb-4"
        >
          ← {t('appointments.backToAppointments')}
        </Button>
        <EnhancedAppointmentScheduler 
          member={member} 
          onSuccess={handleScheduleSuccess} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('appointments.yourAppointments')}</h2>
          <p className="text-gray-600">{t('appointments.manageAndView')}</p>
        </div>
        <Button 
          onClick={() => setShowScheduler(true)}
          className="bg-teal-600 hover:bg-teal-700"
          size="lg"
        >
          <Plus className="h-5 w-5 mr-2" />
          {t('appointments.bookNewAppointment')}
        </Button>
      </div>
      
      <AppointmentHistory 
        member={member}
        appointments={appointments}
        onUpdate={fetchData}
      />
    </div>
  );
};

const MembershipSection = ({ member, membershipPlan, allPlans, payments, fetchData }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // Helper to safely format membership dates
  const formatMembershipDate = (dateString) => {
    if (!dateString) return t('membership.notSet');
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return t('membership.pendingActivation');
      }
      return date.toLocaleDateString();
    } catch {
      return t('membership.notSet');
    }
  };

  const handlePurchasePlan = async (planId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/subscriptions/create-checkout`, {
        plan_id: planId,
        origin_url: origin
      });
      
      // Check if in TEST mode and warn user
      if (response.data.is_test_mode) {
        console.warn('⚠️ Stripe is in TEST MODE');
        toast.warning(response.data.payment_mode_warning || 'Test mode - no real charges will be made', {
          duration: 5000
        });
      }
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {membershipPlan ? (
        <>
          {/* Current Membership Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('membership.currentMembership')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold text-teal-600">{membershipPlan.name}</h3>
                  <p className="text-gray-600">{membershipPlan.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('membership.startDate')}</p>
                    <p className="font-semibold">{formatMembershipDate(member.membership_start_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('membership.endDate')}</p>
                    <p className="font-semibold">{formatMembershipDate(member.membership_end_date)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Manager - Cancel/Reactivate */}
          <SubscriptionManager 
            member={member}
            membershipPlan={membershipPlan}
            onUpdate={() => fetchData && fetchData()}
          />

          {/* Payment Methods - Manage saved cards */}
          <PaymentMethods onUpdate={() => fetchData && fetchData()} />

          {/* Renewal Component (for non-subscription members) */}
          {!member.stripe_subscription_id && (
            <MembershipRenewal 
              member={member} 
              membershipPlan={membershipPlan}
              onRenewSuccess={() => fetchData && fetchData()}
            />
          )}

          {/* Payment History */}
          <PaymentHistory payments={payments} />
        </>
      ) : (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('membership.choosePlan')}</h2>
            <p className="text-gray-600">{t('membership.selectPlanDescription')}</p>
          </div>
          
          <PlansCatalog 
            member={member}
            onPurchaseSuccess={() => window.location.reload()}
          />
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('payments.paymentHistory')}</CardTitle>
          <CardDescription>{t('payments.allTransactions')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payments.date')}</TableHead>
                <TableHead>{t('payments.amount')}</TableHead>
                <TableHead>{t('payments.method')}</TableHead>
                <TableHead>{t('payments.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500">{t('payments.noPaymentHistory')}</TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="font-medium">${payment.amount.toFixed(2)}</TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell>
                      <Badge variant={payment.payment_status === 'completed' ? 'default' : 'secondary'}>
                        {payment.payment_status === 'completed' ? t('payments.completed') : 
                         payment.payment_status === 'pending' ? t('payments.pending') : 
                         t('payments.failed')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const ProfileSection = ({ member, user, fetchData }) => {
  return (
    <ProfileManagement 
      user={user}
      member={member}
      onUpdateSuccess={fetchData}
    />
  );
};

export default PatientDashboard;
